import { FC, useEffect, useMemo } from 'react'

import {
  ActForm,
  ApprovalState,
  useCommentsLogByEntityIdQuery,
  useContract,
  useCoordinator,
  UUID,
} from '@builddocs/data-management'
import { DocsTypes } from '@builddocs/shared/feature-pdf-viewer'
import { Box, Skeleton, Stack, Typography } from '@mui/material'

import AddComment from './AddComment'
import CommentsHistoryList from './CommentsHistoryList'
import CommentsList from './CommentsList'

interface OwnProps {
  onDeviate?: (
    body: Record<string, any>,
    additionalInfo: Record<string, any>,
    onSuccess: () => any,
    query: Record<string, any>,
  ) => void
  commentEntity:
    | {
        place: string
        docType: string
        docId: UUID
        name: string
      }
    | DocsTypes
  closingId: UUID
  title?: string | null
  onChangeCommentCount?: (count: number) => void
  forceContractId?: UUID
  showHistory?: boolean
}

type Props = OwnProps

export const CommentsViewer: FC<Props> = ({
  onDeviate,
  commentEntity,
  closingId,
  title = 'Замечания к документу',
  onChangeCommentCount,
  forceContractId,
  showHistory,
}) => {
  const { contractId } = useCoordinator() as { contractId: string }

  const {
    getRights: { canInspect, canSignContract, canSendActToSigning, canSignAct },
  } = useContract(forceContractId || contractId)

  const currentCommentEntity = useMemo(() => {
    return 'place' in commentEntity
      ? commentEntity
      : {
          place: '',
          docType: commentEntity['actType'] ?? commentEntity['documentType'],
          docId: commentEntity.id,
          name: commentEntity['closingName'] ?? commentEntity.name,
        }
  }, [commentEntity])

  const {
    data: comments = [],
    refetch: refreshComments,
    isLoading,
  } = useCommentsLogByEntityIdQuery({
    externalEntityId: currentCommentEntity.docId,
  })

  const documentType = commentEntity['actType'] ?? commentEntity['documentType']
  const canCreateComment =
    !('approvalStatus' in commentEntity) ||
    commentEntity.approvalStatus === ApprovalState.REJECTED ||
    commentEntity.approvalStatus === ApprovalState.APPROVAL ||
    commentEntity.approvalStatus === ApprovalState.SIGNING

  useEffect(() => {
    onChangeCommentCount && onChangeCommentCount(comments.length)
  }, [comments])

  if (isLoading) {
    return <Skeleton />
  }

  return (
    <Box
      height="100%"
      style={{ overflowY: 'hidden', overflowX: 'hidden' }}
      sx={{ gap: 1, display: 'flex', flexDirection: 'column' }}
      width="100%"
    >
      {title && (
        <Typography gutterBottom variant="h6">
          {title}
        </Typography>
      )}
      {canCreateComment && (
        <AddComment
          canInspect={
            canInspect || canSignContract || canSendActToSigning || canSignAct
          }
          closingId={closingId}
          commentEntity={currentCommentEntity}
          isShowButtonForAll={ActForm[documentType]}
          refreshComments={refreshComments}
          onDeviate={onDeviate}
        />
      )}
      <Stack gap={2} sx={{ overflowY: 'auto', height: '100%' }}>
        <CommentsList
          comments={comments}
          forceContractId={forceContractId}
          isLoading={isLoading}
          refreshComments={refreshComments}
        />
        {showHistory && (
          <CommentsHistoryList
            externalEntityInfo={currentCommentEntity.docType}
            externalId={currentCommentEntity.docId}
            refreshComments={refreshComments}
          />
        )}
      </Stack>
    </Box>
  )
}

export default CommentsViewer
